<template>
  <div class="checkout-success-view">
    <div v-if="loading" class="p-4">
      <loading-spinner />
    </div>

    <div v-else-if="subscription == null" class="message message-error">
      <div class="checkout-success-view__icon">⚠️</div>
      <p class="checkout-success-view__text">
        {{ $t("errors.subscriptionNotLoaded") }}
      </p>
    </div>

    <div v-else class="p-4">
      <div class="checkout-success-view__icon">🎉</div>
      <h1>{{ $t("headings.orderComplete") }}</h1>
      <p class="checkout-success-view__text">
        {{ $t("texts.orderConfirmation") }}
      </p>

      <div class="ta-center mt-6">
        <router-link class="button" :to="nextPageLink">
          {{ $t("labels.startUsingSimcision") }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useSubscriptions } from "@/api/subscriptions.store";
import { showError } from "@core/ui/alert/alert";
import { useUser } from "@/api/user.store";

import LoadingSpinner from "@core/ui/LoadingSpinner.vue";

import type { Subscription } from "@/api/subscriptions.store";
import { useOrganizations } from "@manager/organizations/organizations.store";

const route = useRoute();
const { loadUser, user } = useUser();
const { getOrganizations, ownedOrganizations } = useOrganizations({}, { user });

// const query = computed(() => route.query);
const loading = ref(true);

const subscriptionStore = useSubscriptions();

const subscriptions = ref<Subscription[]>([]);
const subscriptionId = ref<number | null>(null);

const subscription = computed(() => {
  return subscriptions.value.find(
    (sub) =>
      sub.id == subscriptionId.value
  );
});

const boughtOrganization = computed(() => {
  return ownedOrganizations.value.find((org) => org.subscriptionId === subscription.value?.id) || null;
});

const nextPageLink = computed(() => {
  if (boughtOrganization.value) {
    return { name: "settings-organization", params: { organizationSlug: boughtOrganization.value.slug } };
  }
  return { name: "models-explorer" };
});

let sessionCheckInterval: null | number | NodeJS.Timeout = null;
async function checkSessionStatus(sessionId: string) {
  sessionCheckInterval = setInterval(async () => {
    try {
      const session = await subscriptionStore.getStripeCheckoutStatus(sessionId);
      if (session == null) {
        clearSessionCheckInterval();
        showError("canceled or expired");
      } else if (session.status === "complete") {
        if (session.subscriptionId != null) {
          subscriptionId.value = session.subscriptionId;
          clearSessionCheckInterval();
          loading.value = true;
          await subscriptionStore.getSubscriptions();

          // reload user to get updated subscription
          await loadUser();
          getOrganizations();

          loading.value = false;
        } else {
          console.log("payment not yet through");
        }
      }
    } catch (e) {
      clearSessionCheckInterval();
      showError(e as Error);
    }
  }, 1000);
}
function clearSessionCheckInterval() {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    loading.value = false;
    sessionCheckInterval = null;
  }
}
onBeforeUnmount(clearSessionCheckInterval);

onMounted(async () => {
  subscriptions.value = await subscriptionStore.getSubscriptions();
  checkSessionStatus(route.query.session_id as string);
});
</script>

<style lang="scss">
.checkout-success-view {
  max-width: 40rem;
  margin: auto;
  background-color: var(--color-background-deep);
  padding: 3rem;
  border-radius: 2rem;
  text-align: center;

  &__text {
    font-size: 16px;
    line-height: 1.5;
  }

  &__icon {
    font-size: 4rem;
    text-align: center;
  }
}
</style>
